import React, { useEffect, useState } from 'react';
import PageLoader from '../PageLoader';
import PricingSection from '../../pages/subscriptionPlan/components/PricingSection';
import { fetchSubscriptionPlans } from '../../services/subscription';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import { usePayment } from '../../hooks/usePayment';
import { useSelector } from 'react-redux';

function PricingModal({ isOpen, handleClose }) {
  const subscription = useSelector((state) => state?.userInfo?.userInfo?.data?.subscription)
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(subscription?.plan?.period ?? 'monthly');
  const [confirmModal, setConfirmModal] = useState(null);
  const { initiatePayment } = usePayment();

  const getPlans = async () => {
    try {
      setLoading(true);
      const response = await fetchSubscriptionPlans();
      setPlans(response.plans);
    } catch (error) {
      toast.error('Failed to fetch subscription plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) getPlans();
  }, [isOpen]);

  // ── handle plan selection ───────────────────────────────────────
  const handleSelectPlan = (plan, type) => {
    setConfirmModal({ plan, type });
  };

  // ── confirm upgrade ──────────────────────────────────────────────
  const handleConfirmUpgrade = async () => {
    const { plan } = confirmModal;
    const activeBilling = plan.billing.find((b) => b.period === billing);

    if (!activeBilling) {
      toast.error('Plan not available for selected billing period');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axiosInstanceV2.post(
        '/subscription/upgrade',
        {
          razorpay_plan_id: activeBilling.razorpay_plan_id,
          billing_period: billing,
          is_recurring: true,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const result = response.data.data;

      if (result.type === 'upgrade') {
        setConfirmModal(null);
        handleClose();

        initiatePayment({
          billingDetails: result.billing_details ?? {},
          plan: {
            razorpay_plan_id: activeBilling.razorpay_plan_id,
            title: plan.name,
            billingPeriod: billing,
          },
          paymentType: 'recurring',
          paymentMethod: 'card',
        });
      }

      if (result.type === 'downgrade_scheduled') {
        toast.success(
          `Downgrade to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`
        );
        setConfirmModal(null);
        handleClose();
        // onSubscriptionUpdated?.();
      }

    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Failed to update subscription');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-top modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Change Subscription Plan</h5>
              <button type="button" className="btn-close" onClick={handleClose} />
            </div>

            <div className="modal-body w-100">
              {loading ? (
                <PageLoader />
              ) : (
                <PricingSection
                  billing={billing}
                  setBilling={setBilling}
                  plans={plans}
                  hideFreePlan={true}
                  onSelectPlan={handleSelectPlan}
                />
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── confirmation modal ──────────────────────────────────── */}
      {confirmModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {confirmModal.type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmModal(null)}
                />
              </div>

              <div className="modal-body">
                {confirmModal.type === 'upgrade' ? (
                  <p>
                    You're upgrading to <strong>{confirmModal.plan.name}</strong>.
                    Your current plan will be cancelled and the new plan will
                    activate immediately after payment.
                  </p>
                ) : (
                  <p>
                    You're downgrading to <strong>{confirmModal.plan.name}</strong>.
                    This change will take effect at the end of your current
                    billing cycle. You'll keep your current plan benefits until then.
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setConfirmModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary-modal"
                  onClick={handleConfirmUpgrade}
                >
                  {confirmModal.type === 'upgrade' ? 'Proceed to Payment' : 'Confirm Downgrade'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PricingModal;